import { AutocompleteFormComponent } from "components/Form/components/Autocomplete/AutocompleteFormComponent";
import { getUpdatedData } from "components/Form/components/SchemaForm/SchemaFormService";
import { CustomFilterValues } from "containers/AdvancedSearch/AdvancedSearch";
import { DeleteFieldButton } from "containers/Filter/FilterBuilder/components/FilterFragment";
import { FilterGroup } from "containers/Filter/FilterBuilder/components/FilterGroup";
import { emptyField } from "containers/Filter/FilterBuilder/constants";
import { useMatchingFields } from "containers/Filter/FilterBuilder/useMatchingFields";
import { useMatchingRelations } from "containers/Filter/FilterBuilder/useMatchingRelations";
import { get } from "lodash";
import { Field } from "react-final-form";

interface ExistsFilterFieldProps {
    // The exists node: either `{exists: {relation, where}}` or `{not: {exists: {...}}}`.
    elementData: any;
    index: number;
    parentPath: string;
    // The sibling array this node lives in (get(formData, parentPath)).
    parentData: any[];
    setData: (value: CustomFilterValues) => void;
    formData: CustomFilterValues;
    // The resource table the OUTER filter is scoped to — used to enumerate the
    // subrecord relations offered in the dropdown.
    activeTab?: string;
    deleteGroup?: () => void;
}

const modeOptions = [
    { value: "has", label: "Has related" },
    { value: "hasNo", label: "Has no related" },
];

/**
 * A single "has related record" filter row (reverse EXISTS semijoin).
 *
 * Renders three parts:
 *  1. a has / has-no toggle (positive `exists` vs `not`-wrapped NOT EXISTS),
 *  2. a relation dropdown (subrecord relations of the outer tab), and
 *  3. once a relation is picked, a nested filter builder for the child `where`,
 *     scoped to the relation's own fields — recursing the same FilterGroup UI.
 *
 * The react-final-form values ARE the backend filter grammar verbatim (see
 * CustomFilterField), so writing an `{exists: {...}}` / `{not: {exists: {...}}}`
 * node into the form tree serialises for free. All paths below are absolute within
 * that grammar object so both getUpdatedData writes and `<Field name>` bindings line
 * up. See operator-task-exists-filter-graphql-advsearch-fe-parity (Part 3).
 */
export const ExistsFilterField = ({
    elementData,
    index,
    parentPath,
    parentData,
    setData,
    formData,
    activeTab,
    deleteGroup,
}: ExistsFilterFieldProps) => {
    const isNegated = elementData?.not?.exists !== undefined;
    const spec = isNegated ? elementData.not.exists : elementData.exists;
    const relation: string | null = spec?.relation ?? null;

    const childPath = `${parentPath}[${index}]`;
    const existsPath = isNegated ? `${childPath}.not.exists` : `${childPath}.exists`;
    const relationPath = `${existsPath}.relation`;
    const wherePath = `${existsPath}.where`;

    const relationOptions = useMatchingRelations({ activeTab });
    // Fields available inside the nested `where`, resolved for the picked relation.
    const { matchingFields, filteredFieldList } = useMatchingFields({
        activeTab: relation ?? "",
    });

    const whereData = get(formData, wherePath) ?? { and: [emptyField] };

    // Rebuild the whole node when the has/has-no toggle flips or the relation
    // changes, so the grammar shape (`exists` vs `not.exists`) stays valid.
    const wrapNode = (nextSpec: any, negated: boolean) =>
        negated ? { not: { exists: nextSpec } } : { exists: nextSpec };

    const onToggleMode = (value: string) => {
        const negated = value === "hasNo";
        if (negated === isNegated) {
            return;
        }
        setData(getUpdatedData(formData, childPath, wrapNode(spec, negated)));
    };

    const onChangeRelation = (value: string) => {
        // A new relation means a new field set, so reset the nested where.
        const nextSpec = { relation: value, where: { and: [emptyField] } };
        setData(getUpdatedData(formData, childPath, wrapNode(nextSpec, isNegated)));
    };

    const onDelete = () => {
        const newValue = [...parentData];
        newValue.splice(index, 1);
        if (newValue.length) {
            setData(getUpdatedData(formData, parentPath, newValue));
        } else {
            deleteGroup?.();
        }
    };

    return (
        <div className="existsFilterField fieldGroup">
            <div className="filterField">
                <AutocompleteFormComponent
                    fieldProps={{
                        label: "",
                        options: { list: modeOptions },
                        customKey: `${childPath}-mode`,
                        // Required has/has-no toggle — never clearable (a clear
                        // "✕" indicator both makes no sense here and stole ~22px,
                        // squeezing "Has no related" into a premature ellipsis).
                        clearable: false,
                        fieldWidth: 180,
                    }}
                    input={{
                        name: `${childPath}-mode`,
                        value: isNegated ? "hasNo" : "has",
                        onChange: onToggleMode,
                        onBlur: () => {},
                        onFocus: () => {},
                    }}
                    meta={{}}
                    fieldAttr={{}}
                />
                <Field
                    name={relationPath}
                    validate={(value) => (value ? undefined : "Required.")}
                >
                    {({ input, meta }) => (
                        <AutocompleteFormComponent
                            fieldProps={{
                                label: "",
                                options: { list: relationOptions },
                                customKey: `${relationPath}-${relation}`,
                                fieldWidth: 240,
                            }}
                            input={{
                                ...input,
                                onChange: onChangeRelation,
                                value: relation,
                            }}
                            meta={meta}
                            fieldAttr={{ placeholder: "related record" }}
                        />
                    )}
                </Field>
                <div className="deleteFieldButtonContainer">
                    <DeleteFieldButton onDelete={onDelete} />
                </div>
            </div>
            {relation && (
                <div className="existsFilterWhere">
                    <FilterGroup
                        // Remount when the relation changes: picking a new relation
                        // resets `where` to {and:[…]}, and FilterGroup derives its
                        // group path from state — a fresh mount keeps that in sync.
                        key={`${existsPath}.where.${relation}`}
                        groupPath={wherePath}
                        elementData={whereData}
                        formData={formData}
                        setData={setData}
                        parentPath=""
                        matchingFields={matchingFields}
                        filteredFieldList={filteredFieldList}
                        parentData={whereData}
                        activeTab={relation}
                    />
                </div>
            )}
        </div>
    );
};
